import { FormattedMessage, useIntl } from 'react-intl';
import {
  Col,
  Form,
  Divider,
  Input,
  Row,
  Typography,
  Select,
  FormInstance,
} from 'antd';

import { useDefaultEmptyFieldError } from '@/hooks';

import FloatingFormItem from '@/components/_common/FloatingFormItem';
import { InputDecimal } from '@/components/_common';
import CestFormField from '@/components/_common/CestFormField';
import NcmFormField from '@/components/_common/NcmFormField';
import {
  ipiCstOptions,
  optionsCsosnCodes,
  optionsCstCodes,
  pisCofinsCstOptions,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.const';
import SelectInvoiceClassificationTax from '@/components/_common/TaxReform/SelectInvoiceClassificationTax';
import SelectInvoiceCST from '@/components/_common/TaxReform/SelectInvoiceCST';

const { Title } = Typography;

interface NcmOutgoingFormProps {
  form: FormInstance;
  isModal?: boolean;
}

const NcmOutgoingForm = ({ form, isModal }: NcmOutgoingFormProps) => {
  const intl = useIntl();

  // IS (Imposto Seletivo, UB01-UB11) — fora da NT v1.40; reativar quando
  // entrar em vigor.
  // const selectiveTaxCst = Form.useWatch('selectiveTaxCst', form);
  const ibsCbsCst = Form.useWatch('ibsCbsCst', form);

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  return (
    <Form form={form}>
      <Title level={isModal ? 5 : 3}>
        <FormattedMessage id="ncm.outgoing" />
      </Title>

      <Row gutter={isModal ? [8, 8] : [16, 16]}>
        <Col span={24} md={6} lg={6} xxl={6}>
          <NcmFormField
            onChange={() => null}
            initialValue={form.getFieldValue('code')}
            label={intl.formatMessage({ id: 'ncm.domain.code' })}
            fieldName="code"
          />
        </Col>

        <Col span={24} md={12} lg={12} xxl={12}>
          <FloatingFormItem
            name="description"
            label={intl.formatMessage({ id: 'ncm.domain.description' })}
            rules={[defaultEmptyFieldError]}
          >
            <Input.TextArea
              maxLength={300}
              autoSize={{ minRows: 1, maxRows: 3 }}
              showCount
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={6} lg={6} xxl={6}>
          <CestFormField
            onChange={() => null}
            required={false}
            initialValue={form.getFieldValue('cest')}
            fieldName="cest"
          />
        </Col>

        <Col
          {...(isModal ? { span: 24 } : { span: 24, md: 14, lg: 9, xxl: 9 })}
        >
          <FloatingFormItem
            name="taxesPercentage"
            label={intl.formatMessage({ id: 'ncm.domain.taxesPercentage' })}
            initialValue={0}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>
      </Row>

      {!isModal && <Divider />}

      <Title level={isModal ? 5 : 3}>
        <FormattedMessage id="ncm.icms/csosn" />
      </Title>

      <Row gutter={isModal ? [8, 8] : [16, 16]}>
        <Col {...(isModal ? { span: 16 } : { span: 24, lg: 8, xxl: 6 })}>
          <FloatingFormItem
            name="modalityDeterminationBcIcms"
            label={intl.formatMessage({
              id: 'ncm.domain.modalityDeterminationBcIcms',
            })}
          >
            <Select allowClear>
              <Select.Option value={0}>
                <FormattedMessage id="ncm.domain.modalityDeterminationBcIcms.0" />
              </Select.Option>
              <Select.Option value={1}>
                <FormattedMessage id="ncm.domain.modalityDeterminationBcIcms.1" />
              </Select.Option>
              <Select.Option value={2}>
                <FormattedMessage id="ncm.domain.modalityDeterminationBcIcms.2" />
              </Select.Option>
              <Select.Option value={3}>
                <FormattedMessage id="ncm.domain.modalityDeterminationBcIcms.3" />
              </Select.Option>
            </Select>
          </FloatingFormItem>
        </Col>

        <Col {...(isModal ? { span: 8 } : { span: 24, md: 8, lg: 8, xxl: 6 })}>
          <FloatingFormItem
            name="cstIcms"
            label={intl.formatMessage({ id: 'ncm.domain.cstIcms' })}
          >
            <Select
              allowClear
              options={optionsCstCodes?.map((code) => ({
                value: code.code,
                label: code.code,
              }))}
            />
          </FloatingFormItem>
        </Col>

        <Col {...(isModal ? { span: 12 } : { span: 24, md: 8, lg: 8, xxl: 6 })}>
          <FloatingFormItem
            name="csosnIcms"
            label={intl.formatMessage({ id: 'ncm.domain.csosnIcms' })}
          >
            <Select
              allowClear
              options={optionsCsosnCodes?.map((code) => ({
                value: code.code,
                label: code.code,
              }))}
            />
          </FloatingFormItem>
        </Col>

        <Col {...(isModal ? { span: 12 } : { span: 24, md: 8, lg: 8, xxl: 6 })}>
          <FloatingFormItem
            name="aliquotIcms"
            label={intl.formatMessage({ id: 'ncm.domain.aliquotIcms' })}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col {...(isModal ? { span: 12 } : { span: 24, md: 8, lg: 8, xxl: 6 })}>
          <FloatingFormItem
            name="mva"
            label={intl.formatMessage({ id: 'ncm.domain.mva' })}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col {...(isModal ? { span: 12 } : { span: 24, md: 8, lg: 8, xxl: 6 })}>
          <FloatingFormItem
            name="reductionIcms"
            label={intl.formatMessage({ id: 'ncm.domain.reductionIcms' })}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>
      </Row>

      {!isModal && <Divider />}

      <Title level={isModal ? 5 : 3}>
        <FormattedMessage id="ncm.pis/cofins" />
      </Title>

      <Row gutter={[16, 16]}>
        <Col span={24} md={8} lg={8} xxl={6}>
          <FloatingFormItem
            name="cstPis"
            label={intl.formatMessage({ id: 'ncm.domain.cstPis' })}
          >
            <Select allowClear options={pisCofinsCstOptions} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8} lg={8} xxl={6}>
          <FloatingFormItem
            name="aliquotPis"
            label={intl.formatMessage({ id: 'ncm.domain.aliquotPis' })}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8} lg={8} xxl={6}>
          <FloatingFormItem
            name="cstCofins"
            label={intl.formatMessage({ id: 'ncm.domain.cstCofins' })}
          >
            <Select allowClear options={pisCofinsCstOptions} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8} lg={8} xxl={6}>
          <FloatingFormItem
            name="aliquotCofins"
            label={intl.formatMessage({ id: 'ncm.domain.aliquotCofins' })}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>
      </Row>

      {!isModal && <Divider />}

      <Title level={isModal ? 5 : 3}>
        <FormattedMessage id="ncm.ipi" />
      </Title>

      <Row gutter={[16, 16]}>
        <Col span={24} md={8} lg={8} xxl={6}>
          <FloatingFormItem
            name="aliquotIpi"
            label={intl.formatMessage({ id: 'ncm.domain.aliquotIpi' })}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8} lg={8} xxl={6}>
          <FloatingFormItem
            name="cstIpi"
            label={intl.formatMessage({ id: 'ncm.domain.cstIpi' })}
          >
            <Select
              allowClear
              options={ipiCstOptions?.map((code) => ({
                value: code.value,
                label: code.label,
              }))}
            />
          </FloatingFormItem>
        </Col>
      </Row>

      {/* IS (Imposto Seletivo, UB01-UB11) — fora da NT v1.40; reativar
          quando entrar em vigor. */}
      {/* {!isModal && <Divider />}

      <Title level={isModal ? 5 : 3}>
        <FormattedMessage id="ncm.is" />
      </Title>

      <Row gutter={[16, 16]}>
        <Col span={24} sm={12} md={9}>
          <FloatingFormItem
            name="selectiveTaxCst"
            label="CST IS"
            getValueFromEvent={(value) => {
              form.setFieldValue('selectiveTaxClassificationCode', undefined);
              return value;
            }}
          >
            <SelectInvoiceCST
              queryParams={{ cstType: 'SELECTIVE_TAX' }}
              allowClear
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} md={9}>
          <FloatingFormItem
            label="Classificação Tributária IS"
            name="selectiveTaxClassificationCode"
          >
            <SelectInvoiceClassificationTax
              queryParams={{
                classificationType: 'SELECTIVE_TAX',
                cstCode: selectiveTaxCst,
              }}
              allowClear
              disabled={!selectiveTaxCst}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} md={6}>
          <FloatingFormItem name="selectiveTaxAliquot" label="Alíquota IS (%)">
            <InputDecimal />
          </FloatingFormItem>
        </Col>
      </Row> */}

      {!isModal && <Divider />}

      <Title level={isModal ? 5 : 3}>
        <FormattedMessage id="ncm.ibsCbs" />
      </Title>

      <Row gutter={[16, 16]}>
        <Col span={24} sm={12}>
          <FloatingFormItem
            name="ibsCbsCst"
            label="CST IBS/CBS"
            getValueFromEvent={(value) => {
              form.setFieldValue('ibsCbsTaxClassification', undefined);
              return value;
            }}
          >
            <SelectInvoiceCST queryParams={{ cstType: 'IBS_CBS' }} allowClear />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12}>
          <FloatingFormItem
            name="ibsCbsTaxClassification"
            label="Classificação Tributária"
          >
            <SelectInvoiceClassificationTax
              queryParams={{
                classificationType: 'IBS_CBS',
                cstCode: ibsCbsCst,
              }}
              allowClear
              disabled={!ibsCbsCst}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} md={6}>
          <FloatingFormItem
            label="Alíquota IBS UF (%)"
            name="ibsUfAliquotDefault"
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} md={6}>
          <FloatingFormItem
            label="Alíquota IBS Muni. (%)"
            name="ibsMunicipalAliquotDefault"
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} md={6}>
          <FloatingFormItem label="Alíquota CBS (%)" name="cbsAliquot">
            <InputDecimal />
          </FloatingFormItem>
        </Col>
      </Row>

      {!isModal && <Divider />}
    </Form>
  );
};

export default NcmOutgoingForm;
